import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { MessagingState, ConversationPreview, Message } from "../../types/messaging";
// Assume an API client/helper exists or create one
import apiClient from "../../utils/apiClient"; // Adjust path as needed
import { RootState } from "../../store/store";
import { isEqual, unionWith } from "lodash"; // Import lodash helper

// --- Initial State ---
// Defines the starting state for the messaging feature before any data is loaded.
const initialState: MessagingState = {
    conversations: {
        items: [],
        loading: "idle", // Not started loading yet
        error: null,
    },
    messages: {}, // Start with an empty object for messages per conversation
    activeConversationId: null, // No conversation selected initially
    sendMessageStatus: "idle",
    sendMessageError: null,
    createConversationStatus: "idle",
    createConversationError: null,
};

// --- Async Thunks (API Calls) ---

// 1. Fetch Conversations
// Fetches the list of conversations for the current user.
export const fetchConversations = createAsyncThunk<
    ConversationPreview[], // Type of the successful return value
    void, // Type of the argument passed (none needed here)
    { rejectValue: string } // Type for rejected state payload
>(
    "messaging/fetchConversations", // Action type prefix
    async (_, { rejectWithValue, getState }) => {
        try {
            // console.log("Attempting to fetch conversations...");

            // Get the current user ID from the auth state
            const state = getState() as RootState;
            // Fix: access user.id instead of userId directly
            const currentUserId = state.auth.user?.id;

            if (!currentUserId) {
                return rejectWithValue("You must be logged in to fetch conversations.");
            }

            // Include userId as a query parameter as a workaround
            const response = await apiClient.get<ConversationPreview[]>(
                `/conversations?userId=${currentUserId}`,
            );
            // console.log("Conversations fetched:", response.data);
            // Sort by most recent activity (descending updatedAt)
            return response.data.sort((a, b) => b.updatedAt - a.updatedAt);
        } catch (error: any) {
            console.error("Failed to fetch conversations:", error);
            const message =
                error.response?.data?.message || error.message || "Failed to fetch conversations";
            return rejectWithValue(message); // Pass error message on failure
        }
    },
);

// 2. Fetch Messages for a Conversation
// Fetches the messages for a specific conversationId.
export const fetchMessages = createAsyncThunk<
    { conversationId: string; messages: Message[] }, // Return both ID and messages
    string, // Argument is the conversationId (string)
    { rejectValue: string }
>("messaging/fetchMessages", async (conversationId, { rejectWithValue, getState }) => {
    if (!conversationId) {
        return rejectWithValue("No conversation ID provided.");
    }
    try {
        console.log(`Attempting to fetch messages for conversation: ${conversationId}`);

        // Get the current user ID for the fallback query parameter
        const state = getState() as RootState;
        const currentUserId = state.auth.user?.id;
        if (!currentUserId) {
            return rejectWithValue("You must be logged in to fetch messages.");
        }

        // Include userId as a query parameter as a fallback
        const response = await apiClient.get<{ messages: Message[] }>(
            `/conversations/${conversationId}/messages?userId=${currentUserId}`,
        );
        const messages = response.data.messages.reverse();
        console.log(`Messages fetched for ${conversationId}:`, messages);
        return { conversationId, messages };
    } catch (error: any) {
        console.error(`Failed to fetch messages for ${conversationId}:`, error);
        const message =
            error.response?.data?.message || error.message || "Failed to fetch messages";
        return rejectWithValue(message);
    }
});

// 3. Send Message
// Sends a new message to a specific conversation.
export const sendMessage = createAsyncThunk<
    Message, // Returns the newly created message on success
    { conversationId: string; content: string }, // Args: conversationId and message content
    { rejectValue: string }
>("messaging/sendMessage", async ({ conversationId, content }, { rejectWithValue, getState }) => {
    if (!conversationId || !content) {
        return rejectWithValue("Missing conversation ID or content.");
    }
    try {
        console.log(`Attempting to send message to ${conversationId}`);

        // Get the current user ID for the fallback body property
        const state = getState() as RootState;
        const currentUserId = state.auth.user?.id;
        if (!currentUserId) {
            return rejectWithValue("You must be logged in to send messages.");
        }

        // Prepare payload including requesterId fallback
        const payload = {
            content,
            requesterId: currentUserId,
        };

        const response = await apiClient.post<Message>(
            `/conversations/${conversationId}/messages`,
            payload,
        );
        // console.log("Message sent successfully:", response.data);
        return response.data; // The newly created message object from the backend
    } catch (error: any) {
        console.error(`Failed to send message to ${conversationId}:`, error);
        const message = error.response?.data?.message || error.message || "Failed to send message";
        return rejectWithValue(message);
    }
});

// 4. Create Conversation (DEPRECATED - Use findOrCreateConversation)
// Kept for reference or potential other uses, but profile page should use the new thunk.
export const createConversation = createAsyncThunk<
    ConversationPreview, // Returns the new conversation object
    { recipientId: string }, // Argument: ID of the user to chat with
    { rejectValue: { message: string; status: number } }
>("messaging/createConversation", async ({ recipientId }, { rejectWithValue, getState }) => {
    if (!recipientId) {
        // Reject with the object format
        return rejectWithValue({ message: "Recipient ID is required.", status: 400 });
    }
    try {
        console.log(`Attempting to create conversation with ${recipientId}`);

        // Get the current user ID from the auth state
        const state = getState() as RootState;
        const currentUserId = state.auth.user?.id;

        // Log the auth state for debugging
        // console.log("Auth state for debugging:", {
        //     isAuthenticated: state.auth.isAuthenticated,
        //     hasToken: !!state.auth.token,
        //     user: state.auth.user,
        //     userId: currentUserId,
        // });
        if (!currentUserId) {
            // Reject with the object format
            return rejectWithValue({
                message: "You must be logged in to create a conversation.",
                status: 401,
            });
        }
        const payload = { recipientId, requesterId: currentUserId };
        // console.log("CreateConversation payload:", payload);

        // Implementation is commented out, throw error indicating deprecation instead?
        // For type-checking, we'll satisfy the possibility of success, though unreachable.
        // throw new Error("createConversation is deprecated. Use findOrCreateConversation.");
        // Or just let it fall through to the catch block simulation below if needed
        // If the commented-out API call were active, it would return ConversationPreview here.
        // To satisfy TS without uncommenting:
        return undefined as any; // Placeholder to satisfy return type if try block completes
    } catch (error: any) {
        console.error(`Failed to create conversation with ${recipientId}:`, error);
        const message =
            error.response?.data?.message || error.message || "Failed to create conversation";
        // Reject with the object format, including status
        return rejectWithValue({ message, status: error.response?.status || 500 });
    }
});

// 5. Find or Create Conversation (NEW)
// Checks if a conversation exists with the recipient, creates one if not, returns the conversation.
export const findOrCreateConversation = createAsyncThunk<
    ConversationPreview, // Returns the existing or newly created conversation object
    { recipientId: string }, // Argument: ID of the user to chat with
    {
        rejectValue: { message: string; status: number; existingConversationId?: string };
        state: RootState;
    }
>("messaging/findOrCreateConversation", async ({ recipientId }, { rejectWithValue, getState }) => {
    if (!recipientId) {
        // Use the detailed reject format
        return rejectWithValue({ message: "Recipient ID is required.", status: 400 });
    }
    const state = getState(); // No need to cast if ThunkApiConfig is typed
    const currentUserId = state.auth.user?.id;

    if (!currentUserId) {
        // Use the detailed reject format
        return rejectWithValue({
            message: "You must be logged in to start a conversation.",
            status: 401,
        });
    }

    if (currentUserId === recipientId) {
        // Use the detailed reject format
        return rejectWithValue({
            message: "Cannot start a conversation with yourself.",
            status: 400,
        });
    }

    try {
        console.log(`Attempting to find or create conversation with ${recipientId}`);

        // Prepare payload including requesterId fallback for the new endpoint
        const payload = {
            recipientId,
            requesterId: currentUserId, // Send requesterId for fallback
        };
        // console.log("FindOrCreateConversation payload:", payload);

        // Call the new backend endpoint
        const response = await apiClient.post<ConversationPreview>(
            "/conversations/find-or-create", // Use the new route
            payload,
        );

        // console.log("Conversation found or created:", response.data);
        return response.data; // Returns the conversation object (either existing or new)
    } catch (error: any) {
        console.error(`Failed to find or create conversation with ${recipientId}:`, error);

        // Check if it's a 409 Conflict error and extract relevant data
        if (error.response?.status === 409 && error.response.data?.existingConversationId) {
            return rejectWithValue({
                message: error.response.data.message || "Conversation already exists.",
                existingConversationId: error.response.data.existingConversationId,
                status: 409, // Include status for easier checking in component
            });
        }

        // Handle other errors generically
        const message =
            error.response?.data?.message || error.message || "Failed to find/create conversation";
        return rejectWithValue({ message, status: error.response?.status || 500 }); // Include status for other errors too
    }
});

// --- Slice Definition ---
const messagingSlice = createSlice({
    name: "messaging", // Name of the slice
    initialState,
    // Reducers handle synchronous state updates
    reducers: {
        // Action to set the currently active conversation
        setActiveConversation(state, action: PayloadAction<string | null>) {
            // console.log("Setting active conversation:", action.payload);
            state.activeConversationId = action.payload;
            // Optionally reset message send status when changing conversations
            state.sendMessageStatus = "idle";
            state.sendMessageError = null;
        },
        // Synchronously add a message (e.g., after successful send or via WebSocket later)
        addMessage(state, action: PayloadAction<Message>) {
            const message = action.payload;
            const conversationId = message.conversationId;
            // Ensure the message array exists for this conversation
            if (!state.messages[conversationId]) {
                state.messages[conversationId] = { items: [], loading: "idle", error: null };
            }
            // Avoid adding duplicates if the message already exists
            if (!state.messages[conversationId].items.some((m) => m.id === message.id)) {
                console.log(`Adding message ${message.id} to conversation ${conversationId}`);
                state.messages[conversationId].items.push(message);
            }
        },
        // TODO: Add reducers for updating message status (sent, delivered, read) later
    },
    // ExtraReducers handle actions defined outside the slice (like async thunks)
    extraReducers: (builder) => {
        builder
            // --- Fetch Conversations Reducers ---
            .addCase(fetchConversations.pending, (state) => {
                state.conversations.loading = "pending";
                state.conversations.error = null;
            })
            .addCase(fetchConversations.fulfilled, (state, action) => {
                state.conversations.loading = "succeeded";
                state.conversations.items = action.payload; // Replace existing conversations
            })
            .addCase(fetchConversations.rejected, (state, action) => {
                state.conversations.loading = "failed";
                state.conversations.error = action.payload ?? "Unknown error";
            })
            // --- Fetch Messages Reducers ---
            .addCase(fetchMessages.pending, (state, action) => {
                const conversationId = action.meta.arg; // Get conversationId from action meta
                if (conversationId) {
                    // Initialize state for this conversation if it doesn't exist
                    if (!state.messages[conversationId]) {
                        state.messages[conversationId] = {
                            items: [],
                            loading: "idle",
                            error: null,
                        };
                    }
                    // Only set to pending if not already loading (avoids flicker during polling)
                    if (state.messages[conversationId].loading === "idle") {
                        state.messages[conversationId].loading = "pending";
                    }
                    state.messages[conversationId].error = null;
                }
            })
            .addCase(fetchMessages.fulfilled, (state, action) => {
                const { conversationId, messages: fetchedMessages } = action.payload;
                if (conversationId && state.messages[conversationId]) {
                    state.messages[conversationId].loading = "succeeded";
                    const existingMessages = state.messages[conversationId].items;
                    // Add explicit types to unionWith callback parameters
                    const mergedMessages = unionWith<Message>( // Specify type for unionWith
                        existingMessages,
                        fetchedMessages,
                        (msgA: Message, msgB: Message) => msgA.id === msgB.id,
                    );
                    // Add explicit types to sort callback parameters
                    mergedMessages.sort((a: Message, b: Message) => a.createdAt - b.createdAt);
                    state.messages[conversationId].items = mergedMessages;
                }
            })
            .addCase(fetchMessages.rejected, (state, action) => {
                const conversationId = action.meta.arg;
                if (conversationId && state.messages[conversationId]) {
                    state.messages[conversationId].loading = "failed";
                    state.messages[conversationId].error = action.payload ?? "Unknown error";
                }
            })
            // --- Send Message Reducers ---
            .addCase(sendMessage.pending, (state) => {
                state.sendMessageStatus = "pending";
                state.sendMessageError = null;
            })
            .addCase(sendMessage.fulfilled, (state, action) => {
                state.sendMessageStatus = "succeeded";
                const newMessage = action.payload;
                const conversationId = newMessage.conversationId;
                // Add the newly sent message to the state synchronously
                if (!state.messages[conversationId]) {
                    state.messages[conversationId] = { items: [], loading: "idle", error: null };
                }
                // Avoid duplicates if it somehow got added already
                if (!state.messages[conversationId].items.some((m) => m.id === newMessage.id)) {
                    state.messages[conversationId].items.push(newMessage);
                }

                // Also update the conversation preview list
                const convoIndex = state.conversations.items.findIndex(
                    (c) => c.id === conversationId,
                );
                if (convoIndex !== -1) {
                    // Update snippet and timestamp
                    state.conversations.items[convoIndex].lastMessageSnippet =
                        newMessage.content.substring(0, 50) + "...";
                    state.conversations.items[convoIndex].lastMessageTimestamp =
                        newMessage.createdAt;
                    state.conversations.items[convoIndex].updatedAt = newMessage.createdAt;
                    // Move conversation to the top (most recent)
                    const updatedConvo = state.conversations.items.splice(convoIndex, 1)[0];
                    state.conversations.items.unshift(updatedConvo);
                }
            })
            .addCase(sendMessage.rejected, (state, action) => {
                state.sendMessageStatus = "failed";
                state.sendMessageError = action.payload ?? "Unknown error";
            })
            // --- Create Conversation Reducers (Keep for now if used elsewhere) ---
            .addCase(createConversation.pending, (state) => {
                state.createConversationStatus = "pending";
                state.createConversationError = null;
            })
            .addCase(createConversation.fulfilled, (state, action) => {
                state.createConversationStatus = "succeeded";
                const newConversation = action.payload;
                if (!state.conversations.items.some((c) => c.id === newConversation.id)) {
                    state.conversations.items.unshift(newConversation);
                }
            })
            .addCase(createConversation.rejected, (state, action) => {
                state.createConversationStatus = "failed";
                // Update to access message string from the payload object
                state.createConversationError = action.payload?.message ?? "Unknown error";
            })

            // --- Find or Create Conversation Reducers (NEW) ---
            // We might want similar pending/rejected states if we add UI feedback
            // For now, we primarily care about the fulfilled state to update the list.
            .addCase(findOrCreateConversation.fulfilled, (state, action) => {
                const conversation = action.payload; // Existing or newly created
                // Add conversation to the list if it's not already there
                // (Covers both finding an existing one not yet in the list, or adding a new one)
                const existingIndex = state.conversations.items.findIndex(
                    (c) => c.id === conversation.id,
                );
                if (existingIndex === -1) {
                    state.conversations.items.unshift(conversation);
                } else {
                    // Optional: If found, move it to the top? Or update its timestamp?
                    // For now, just ensure it's there.
                }
                // We don't automatically set it active here; the calling component handles that.
            });
        // Optional: Add pending/rejected handlers for findOrCreateConversation if needed
        // .addCase(findOrCreateConversation.pending, (state) => { ... })
        // .addCase(findOrCreateConversation.rejected, (state, action) => { ... });
    },
});

// Export actions (synchronous reducers)
export const { setActiveConversation, addMessage } = messagingSlice.actions;

// Export the reducer function for the store
export default messagingSlice.reducer;
